/**
 * Dev Bypass Utilities
 * Provides mock MSAL functionality for local development without Azure AD
 */

import { getConfig } from "../config";

// Check if we're in dev bypass mode (evaluated after loadConfig in index.tsx)
function computeDevBypass(): boolean {
  const isDev = process.env.NODE_ENV === "development";
  const isLocalhost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const cfg = getConfig();
  const hasCredentials = !!(cfg.clientId && cfg.tenantId);
  const hasLocalhostRedirect =
    cfg.redirectUri.includes("localhost") || cfg.redirectUri === "" || (typeof window !== "undefined" && cfg.redirectUri === window.location.origin);
  return isDev && (!hasCredentials || (isLocalhost && !hasLocalhostRedirect));
}

export const useDevBypass = computeDevBypass();

// Mock account for dev mode
export const devAccount = {
  localAccountId: "dev-user-id",
  name: "Dev User",
  username: "dev@localhost",
  idTokenClaims: {
    roles: ["Portal.Admin", "Portal.User"],
  },
};

// Mock MSAL instance for dev mode
export const devMsalInstance = {
  setActiveAccount: () => {},
  getAllAccounts: () => [devAccount],
  getActiveAccount: () => devAccount,
};

/**
 * Get MSAL accounts - returns mock in dev bypass, real in prod
 */
export function getMsalAccounts(msalAccounts: unknown[]): typeof devAccount[] {
  if (useDevBypass) {
    return [devAccount];
  }
  return msalAccounts as typeof devAccount[];
}

/**
 * Get user ID - returns mock in dev bypass
 */
export function getUserId(account: { localAccountId?: string } | null | undefined): string {
  if (useDevBypass) {
    return "dev-user-id";
  }
  return account?.localAccountId || "";
}

/**
 * Get user roles - returns admin in dev bypass
 */
export function getUserRoles(account: { idTokenClaims?: { roles?: string[] } } | null | undefined): string[] {
  if (useDevBypass) {
    return ["Portal.Admin", "Portal.User"];
  }
  const claims = account?.idTokenClaims as { roles?: string[] } | undefined;
  return claims?.roles || [];
}
